import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { UserDAO } from '../../../core/services/dao/userDAO';
import {
  userIsAffiliate,
  userIsBroker,
  userIsSpecifiedRole,
} from '../../login/login.component';
import { AuthService } from '../../../core/services/auth.service';
import { NavigationEnd, Router } from '@angular/router';
import { PortalService } from '../../portal/portal-service';
import { User } from 'anchor-shared/lib/model/entity/user';
import { CharterStatus } from 'anchor-shared/lib/model/enum/charter-status';

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NavigationComponent implements OnInit, OnDestroy {
  route: string = '';
  navigationType: 'main' | 'portal';
  user: User;

  userAccess: UserAccess = {
    isLoggedIn: false,
    isCustomer: false,
    isCaptain: false,
    isBoatOwner: false,
    isBroker: false,
    isAffiliate: false,
    isCrew: false,
    isAdmin: false,
    isApproved: false,
    isHeadCaptain: false,
    canAccessAdmin: false,
    hasICA: false,
    hasLicense: false,
  };

  navProfileItems: NavListItem[] = [
    {
      label: 'Owner dashboard',
      route: '/manage/bookings-manager',
      routeParams: { activeTab: CharterStatus.approvedPending },
      condition: () =>
        this.userAccess.isBoatOwner && !this.userAccess.canAccessAdmin,
      section: 'account',
      icon: '/prod/nav-dashboard-icon.svg',
    },
    {
      label: 'Admin dashboard',
      action: () => this.navigateToDashboard(),
      condition: () => this.userAccess.canAccessAdmin,
      section: 'account',
      icon: '/prod/nav-dashboard-icon.svg',
    },
    {
      label: 'Account',
      route: '/manage',
      condition: () =>
        this.userAccess.isBoatOwner || this.userAccess.canAccessAdmin,
      section: 'account',
      icon: '/prod/nav-profile-icon.svg',
    },
    {
      label: 'Affiliate Settings',
      route: '/manage/affiliate-dashboard',
      condition: () => this.userAccess.isAffiliate,
      section: 'account',
      icon: '/prod/nav-affiliate-settings-icon.svg',
    },
    {
      label: 'Paystubs',
      route: '/affiliate-paystubs',
      condition: () => this.userAccess.isAffiliate,
      section: 'account',
    },
    {
      label: 'Captain dashboard',
      route: '/manage/messages',
      condition: () => this.userAccess.isCaptain,
      section: 'account',
      icon: '/prod/nav-dashboard-icon.svg',
    },
    {
      label: 'Captain profile',
      route: '/manage',
      condition: () => this.userAccess.isCaptain,
      section: 'account',
      icon: '/prod/nav-profile-icon.svg',
    },
    {
      divider: true,
      condition: () =>
        this.userAccess.isBoatOwner ||
        this.userAccess.canAccessAdmin ||
        this.userAccess.isAffiliate ||
        this.userAccess.isCaptain ||
        this.userAccess.isHeadCaptain,
    },
    {
      label: 'Profile',
      route: '/customer/account',
      condition: () => this.userAccess.isCustomer,
      section: 'account',
      icon: '/prod/nav-profile-icon.svg',
    },
    {
      label: 'Payment Information',
      route: '/customer/payment-information',
      condition: () => this.userAccess.isCustomer,
      section: 'account',
      icon: 'credit_card',
    },
    { divider: true, condition: () => this.userAccess.isCustomer },
    {
      label: 'My Charters',
      route: '/customer/charters',
      condition: () => this.userAccess.isCustomer,
      section: 'account',
      icon: '/prod/nav-my-charters-icon.svg',
    },
    {
      label: 'Messages',
      route: '/customer/messages',
      condition: () => this.userAccess.isCustomer,
      section: 'account',
      icon: '/prod/nav-messages-icon.svg',
    },
    { divider: true, condition: () => this.userAccess.isCustomer },
    {
      label: 'Boat Owner Agreement',
      route:
        'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Boat_Owner_Agreement.pdf',
      condition: () => this.userAccess.isBoatOwner,
      isExternal: true,
      section: 'resources',
      icon: '/prod/nav-bo-agreement-icon.svg',
    },
    {
      label: 'Frequently asked questions',
      route: '/boat-owner-faq',
      condition: () => this.userAccess.isBoatOwner,
      section: 'resources',
      icon: '/prod/nav-faq-icon.svg',
    },
    {
      label: 'Day charter cancellation policy',
      route:
        'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Day_Charter_Cancellation_Policy_08_10_2024.pdf',
      condition: () => this.userAccess.isBoatOwner,
      isExternal: true,
      section: 'resources',
      icon: '/prod/nav-day-charter-agreement-icon.svg',
    },
    {
      label: 'Term charter cancellation policy',
      route:
        'https://anchor-public-media.s3.us-east-2.amazonaws.com/prod/Term_Charter_Cancellation_Policy_08_10_2024.pdf',
      condition: () => this.userAccess.isBoatOwner,
      isExternal: true,
      section: 'resources',
      icon: '/prod/nav-term-charter-agreement-icon.svg',
    },
    { divider: true, condition: () => this.userAccess.isBoatOwner },
    {
      label: 'Contact Anchor',
      route: '/contact-anchor',
      condition: () => this.userAccess.isCustomer,
    },
    {
      label: 'Log out',
      action: () => this.logoutClick(),
      condition: () => this.userAccess.isLoggedIn,
      section: 'control',
      icon: 'power_settings_new',
    },
  ];

  isMobile: boolean;
  subs: Subscription[] = [];

  constructor(
    private userDAO: UserDAO,
    private authService: AuthService,
    private portalService: PortalService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const breakpointObserverSub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
        this.cdr.markForCheck();
      });

    this.setUserAccess();

    const portalSub = this.portalService.emitChange.subscribe((change) => {
      this.user = this.userDAO.getCurrentUser();
      this.userAccess = {
        ...this.userAccess,
        hasICA: this.user?.captain
          ? this.user.captain.icaType === 'Operator'
          : false,
        hasLicense: this.user?.captain ? this.user.captain.uscgLicensed : false,
      };
      this.cdr.markForCheck();
    });

    const authSub = this.authService.loggedInObservable.subscribe(() => {
      this.setUserAccess();
      this.cdr.markForCheck();
    });

    const routerSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.route = event.urlAfterRedirects;
        this.navigationType =
          this.route.startsWith('/manage') ||
          this.route.startsWith('/affiliate-dashboard')
            ? 'portal'
            : 'main';
        this.cdr.markForCheck();
      }
    });

    this.route = this.router.url;
    this.navigationType = this.route.startsWith('/manage') ? 'portal' : 'main';

    this.subs.push(breakpointObserverSub, portalSub, authSub, routerSub);
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  setUserAccess(): void {
    this.user = this.userDAO.getCurrentUser();

    this.userAccess = {
      isLoggedIn: !!this.user,
      isCustomer: this.userDAO.currentUserIs('Customer'),
      isCaptain: !!this.user?.captain,
      isBoatOwner: !!this.user?.boatOwner,
      isBroker: this.user ? userIsBroker(this.user) : false,
      isAffiliate: this.user ? userIsAffiliate(this.user) : false,
      isAdmin: this.userDAO.currentUserIs('Admin'),
      isCrew: this.userDAO.currentUserIs('Crew'),
      isApproved: this.user?.boatOwner ? this.user.boatOwner.approved : false,
      isHeadCaptain: this.user
        ? userIsSpecifiedRole(this.user, 'HeadCaptain')
        : false,
      canAccessAdmin:
        this.user?.roles?.some((role) => role.adminAccess) ?? false,
      hasICA: this.user?.captain
        ? this.user.captain.icaType === 'Operator'
        : false,
      hasLicense: this.user?.captain ? this.user.captain.uscgLicensed : false,
    };
  }

  navigateToDashboard() {
    const route = this.userAccess.isBoatOwner
      ? '/manage/bookings-manager'
      : '/manage/admin';
    this.router.navigate([route]);
  }

  isMaxWidthPage() {
    const maxWidthPaths = ['/vessels/yacht-and-boat-rentals'];
    try {
      return maxWidthPaths.some((path) => this.route.includes(path));
    } catch {
      return false;
    }
  }

  logoutClick(): void {
    const sub = this.authService.logout().subscribe(
      (data) => {
        this.router.navigate(['login']);
      },
      (error) => {
        console.log('logout failed');
      },
    );
    this.subs.push(sub);
  }
}

export interface NavListItem {
  label?: string;
  route?: string;
  routeParams?: { [key: string]: string };
  icon?: string;
  children?: NavListItem[];
  condition?: () => boolean;
  section?: 'menu' | 'dashboard' | 'account' | 'resources' | 'main' | 'control';
  isExternal?: boolean;
  action?: () => void;
  divider?: boolean;
  customClass?: string;
  subOption?: boolean;
  unactivatable?: boolean;
}

export interface UserAccess {
  isLoggedIn: boolean;
  isCustomer: boolean;
  isCaptain: boolean;
  isBoatOwner: boolean;
  isBroker: boolean;
  isAffiliate: boolean;
  isCrew: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  isHeadCaptain: boolean;
  canAccessAdmin: boolean;
  hasICA: boolean;
  hasLicense: boolean;
}
